import {
  CSSProperties,
  FC,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";

type IconProps = {
  name: string;
  className?: string;
  filled?: boolean;
  style?: CSSProperties;
};

// Google Material Symbols icon (font is loaded in public/index.html)
export const Icon: FC<IconProps> = ({
  name,
  className = "",
  filled,
  style,
}) => (
  <span
    className={`material-symbols-outlined ${className}`}
    style={filled ? { fontVariationSettings: "'FILL' 1", ...style } : style}
    aria-hidden="true"
  >
    {name}
  </span>
);

type SectionHeaderProps = {
  eyebrow: string;
  title: string;
  description: string;
  className?: string;
};

export const SectionHeader: FC<SectionHeaderProps> = ({
  eyebrow,
  title,
  description,
  className = "mb-16",
}) => (
  <div
    className={`flex flex-col items-center text-center max-w-2xl mx-auto gap-3 ${className}`}
  >
    <span className="font-label-sm text-label-sm uppercase tracking-wider text-primary font-bold">
      {eyebrow}
    </span>
    <h2 className="font-headline-lg text-headline-lg text-on-surface">
      {title}
    </h2>
    <p className="font-body-md text-body-md text-on-surface-variant">
      {description}
    </p>
  </div>
);

// Fades and slides its content in the first time it scrolls into view.
// Visitors with "reduce motion" enabled see the content immediately.
export const Reveal: FC<{ children: ReactNode }> = ({ children }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || !("IntersectionObserver" in window)) {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.08, rootMargin: "0px 0px -8% 0px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`transition-[opacity,transform] duration-700 ease-out motion-reduce:transition-none motion-reduce:opacity-100 motion-reduce:transform-none ${
        visible ? "opacity-100" : "opacity-0 translate-y-6"
      }`}
    >
      {children}
    </div>
  );
};

export const Stars: FC = () => (
  <>
    {[0, 1, 2, 3, 4].map((i) => (
      <Icon key={i} name="star" className="text-sm" filled />
    ))}
  </>
);
